import { FC, useState, ChangeEvent } from 'react';
import { Select, Box, Typography } from '@material-ui/core';
import { ExpandMore } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useAdmin } from 'store/admin/hooks';
import { setAssignee } from 'http/admin';
import { User } from 'core/types';
import { composeFullName } from 'utils';
import { Admin } from 'store/admin/types';
import useStyles from './MultiAssignee.styles';

interface MultiAssigneeProps {
  applicationIds: string[]; // multiple leads selected
  isAdmin: boolean;
  admins: Admin[];
  users: User[];
}

const MultiAssignee: FC<MultiAssigneeProps> = ({ applicationIds, isAdmin, admins, users }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { setError, setSuccess } = useAdmin();

  const [assignee, setNewAssignee] = useState<string | undefined>(undefined);

  const [loading, setLoading] = useState(false);

  const assigneeOptions = (isAdmin ? admins : users).map(({ id, first_name, last_name }) => ({
    id,
    fullName: composeFullName(first_name, last_name),
  }));

  const setAppAssignee = async (event: ChangeEvent<{ name?: string | undefined; value: unknown }>) => {
    const { value } = event.target;
    const selectedAssignee = (value as string) || undefined;

    setNewAssignee(selectedAssignee);

    if (!selectedAssignee) {
      // Optionally handle "None" selection
      setError(t('No assignee selected'));
      return;
    }
    if (applicationIds.length === 0) {
      setError(t('No leads selected'));
      return;
    }

    setLoading(true);
    try {
      await Promise.all(applicationIds.map((id) => setAssignee(id, selectedAssignee)));
      setSuccess(t('Leads assigned successfully'));
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errMessage = (err as any)?.response?.message || (err as Error).message;

      setError(errMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={classes.multiAssigneeContainer}>
      <Typography>Update {t('pages.lead.sections.header.assignee.title')}</Typography>
      <Select
        native
        IconComponent={() => <ExpandMore className={classes.expandIcon} />}
        variant="outlined"
        onChange={setAppAssignee}
        value={assignee || ''}
        color="primary"
        className={classes.select}
        disabled={loading}
        displayEmpty
        inputProps={{ 'aria-label': t('Select assignee') }}
      >
        <option value="">{t('None')}</option>
        {assigneeOptions.map(({ id, fullName }) => (
          <option key={id} value={id}>
            {fullName}
          </option>
        ))}
      </Select>
    </Box>
  );
};

export default MultiAssignee;
